import { Controller } from "@hotwired/stimulus";

const DEBOUNCE_MS = 400;
// Keep "Saving…" on screen long enough to register — matches form-auto-save.
const DEFAULT_MIN_SAVING_MS = 1000;

// Listens for `input` and `change` bubbling out of the question form,
// debounces a save, and submits the form via Turbo. `input` catches live
// typing (no blur needed); `change` catches programmatic dispatches from
// custom widgets (the inline type picker, tom-select, conditional-display)
// that don't fire `input`. The single debounce timer collapses both into
// one PATCH per pause. The save-indicator's min-saving hold (see
// DEFAULT_MIN_SAVING_MS) keeps the UI from flickering on fast saves.
// Save progress is broadcast as `survey-save` window events so the builder
// header's `save-indicator` reflects it — the same indicator the scoring
// page's form-auto-save controller drives.
//
// The server's `auto_save.turbo_stream.erb` response handles updating the
// canvas display row and the rail item; this controller mirrors nothing else.
//
// Option-row focus: when a save adds or removes an option, that response
// re-renders the whole options container (so a new option's persisted id
// flows back into the DOM). The innerHTML swap destroys whatever option
// input the user is typing in, dropping their caret. We capture the focused
// option input's position + caret on submit-start and, once the swap lands,
// move focus back onto the equivalent input — see captureOptionFocus /
// restoreOptionFocus.
type CapturedFocus = {
  // The live element at submit time. Once it's disconnected we know the
  // container was re-rendered and a restore is warranted; while it stays
  // connected (a pure label/score edit, no swap) we leave focus untouched.
  element: Element;
  index: number;
  fieldKey: string;
  start: number | null;
  end: number | null;
  direction: "forward" | "backward" | "none";
};

export default class extends Controller<HTMLElement> {
  static targets = ["form"];
  static values = {
    minSaving: { type: Number, default: DEFAULT_MIN_SAVING_MS },
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly hasFormTarget: boolean;
  declare readonly minSavingValue: number;

  private debounceTimer: number | null = null;
  private savingStartedAt = 0;
  private capturedFocus: CapturedFocus | null = null;
  private boundOnFormEdit = this.onFormEdit.bind(this);
  private boundOnSubmitStart = () => this.onSubmitStart();
  private boundOnSubmitEnd = (event: Event) => this.onSubmitEnd(event);

  // Target-connected callbacks (not connect/disconnect) because the form
  // arrives after the controller: the card lazy-loads it into a Turbo Frame
  // on first expand.
  formTargetConnected(form: HTMLFormElement) {
    form.addEventListener("input", this.boundOnFormEdit);
    form.addEventListener("change", this.boundOnFormEdit);
    form.addEventListener("lexxy:change", this.boundOnFormEdit);
    form.addEventListener("turbo:submit-start", this.boundOnSubmitStart);
    form.addEventListener("turbo:submit-end", this.boundOnSubmitEnd);
  }

  formTargetDisconnected(form: HTMLFormElement) {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    form.removeEventListener("input", this.boundOnFormEdit);
    form.removeEventListener("change", this.boundOnFormEdit);
    form.removeEventListener("lexxy:change", this.boundOnFormEdit);
    form.removeEventListener("turbo:submit-start", this.boundOnSubmitStart);
    form.removeEventListener("turbo:submit-end", this.boundOnSubmitEnd);
  }

  disconnect() {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private onFormEdit() {
    this.scheduleSave();
  }

  private scheduleSave() {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      if (this.hasFormTarget) this.formTarget.requestSubmit();
    }, DEBOUNCE_MS);
  }

  private onSubmitStart() {
    this.savingStartedAt = Date.now();
    this.captureOptionFocus();
    this.broadcast("saving");
  }

  private async onSubmitEnd(event: Event) {
    const success = (event as CustomEvent).detail?.success ?? false;

    // Restore option-row focus as soon as the swap lands, independent of the
    // "Saving…" min-hold below so the caret returns immediately.
    this.restoreOptionFocus();

    // Hold "Saving…" for a minimum so a near-instant save still registers
    // as a deliberate state change rather than a flicker.
    const remaining = this.minSavingValue - (Date.now() - this.savingStartedAt);
    if (remaining > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, remaining));
    }

    this.broadcast(success ? "saved" : "error");
  }

  // Records which option input holds the caret, by ordinal row position and
  // field name (`label`, `score`, …), so it can be re-found after the
  // options container is re-rendered. Position-based because a newly-added
  // option's field name changes on save (temp index → real id), but its row
  // order is preserved by the server's `order(:position)` reload.
  private captureOptionFocus() {
    this.capturedFocus = null;
    const active = document.activeElement as HTMLElement | null;
    const container = this.optionsContainer();
    if (!active || !container || !container.contains(active)) return;

    const row = active.closest(".nested-fields");
    if (!row) return;
    const index = this.visibleRows(container).indexOf(row as HTMLElement);
    if (index === -1) return;

    const fieldKey = (active.getAttribute("name") || "").match(/\[([^\]]+)\]$/)?.[1];
    if (!fieldKey) return;

    const field = active as HTMLInputElement;
    this.capturedFocus = {
      element: active,
      index,
      fieldKey,
      // selectionStart throws on input types that don't support it (e.g.
      // number score fields) — fall back to focus-only restore.
      start: this.safeSelection(field, "selectionStart"),
      end: this.safeSelection(field, "selectionEnd"),
      direction: (this.trySelectionDirection(field) ?? "none"),
    };
  }

  // After the options re-render, move focus back to the equivalent input.
  // Only acts once the captured element is actually disconnected (the swap
  // happened); a pure label/score edit that triggers no swap leaves the
  // user's live caret alone. Polls a few frames because the stream may render
  // a tick after `turbo:submit-end`.
  private restoreOptionFocus() {
    const captured = this.capturedFocus;
    this.capturedFocus = null;
    if (!captured) return;

    let attempts = 5;
    const tick = () => {
      if (captured.element.isConnected) {
        if (attempts-- > 0) requestAnimationFrame(tick);
        return;
      }
      const container = this.optionsContainer();
      if (!container) return;
      const row = this.visibleRows(container)[captured.index];
      const input = row?.querySelector<HTMLInputElement>(
        `[name$="[${captured.fieldKey}]"]`,
      );
      if (!input) return;
      input.focus();
      if (captured.start !== null && captured.end !== null) {
        try {
          input.setSelectionRange(captured.start, captured.end, captured.direction);
        } catch {
          // Input type doesn't support a selection range — focus is enough.
        }
      }
    };
    tick();
  }

  private optionsContainer(): HTMLElement | null {
    if (!this.hasFormTarget) return null;
    return this.formTarget.querySelector<HTMLElement>(
      '[data-nested-form-target="container"]',
    );
  }

  private visibleRows(container: HTMLElement): HTMLElement[] {
    return Array.from(
      container.querySelectorAll<HTMLElement>(".nested-fields"),
    ).filter((row) => row.style.display !== "none");
  }

  private safeSelection(
    field: HTMLInputElement,
    prop: "selectionStart" | "selectionEnd",
  ): number | null {
    try {
      return field[prop];
    } catch {
      return null;
    }
  }

  private trySelectionDirection(
    field: HTMLInputElement,
  ): "forward" | "backward" | "none" | null {
    try {
      return field.selectionDirection;
    } catch {
      return null;
    }
  }

  private broadcast(state: "saving" | "saved" | "error") {
    window.dispatchEvent(new CustomEvent("survey-save", { detail: { state } }));
  }
}
